// Android 인앱(AudioButtonWebview) 전용: 네이티브 플레이어 메시지 두절(stall) 감지·복구 판단.
//
// 배경: 네이티브는 재생 중 ~250ms 간격으로 progress 메시지를 보내지만, 트랙 종료 시
// "finished"는 소수 회만 전송되고 이후 침묵한다. 일부 Android 기기에서 백그라운드
// (화면 꺼짐) 중 WebView가 얼어 이 메시지들이 유실되면 웹 플레이어는 영구히
// "재생 중" 상태에 갇힌다. 이 모듈은 그 상태를 감지해 어떤 복구를 할지 결정한다.

// 재생 중 정상 수신 간격(~250ms) 대비 충분히 큰 두절 판정 임계값
export const STALL_THRESHOLD_MS = 8_000;
// 감시 주기
export const STALL_WATCHDOG_INTERVAL_MS = 3_000;
// getState 질의가 이 횟수만큼 무응답이면 구버전 바이너리로 보고 폴백한다
export const MAX_UNANSWERED_STATE_QUERIES = 2;

export type StallRecoveryAction =
  // 정상 수신 중이거나 판단 조건 미충족 — 아무것도 하지 않음
  | "none"
  // 경과 시간상 트랙이 이미 끝났음이 확실 — 종료 확정(isEnded) 처리
  | "finalize"
  // 네이티브에 상태 스냅샷 질의(audioAction: getState) — 신버전 바이너리만 응답
  | "queryState"
  // getState 무응답(구버전 바이너리) — 멈춘 "재생 중" UI를 조작 가능한 일시정지로 강등
  | "downgradeToPaused";

export interface StallRecoveryInput {
  isActive: boolean;
  playerStatus: string;
  // 이 인스턴스의 마지막 audioPlayer 메시지 수신 시각(ms). 수신 전이면 재생 요청 시각.
  lastMessageAtMs: number | null;
  nowMs: number;
  // 마지막으로 알고 있는 재생 위치(초). 네이티브가 floor한 정수 초.
  lastKnownTimeSec: number;
  durationSec: number;
  isDocumentVisible: boolean;
  // 마지막 메시지 수신 이후 보낸 getState 질의 횟수
  unansweredStateQueries: number;
  // extraAudioUrl 트랙 여부. 본 트랙 종료 후 루프를 무한 재생(라이브 모드)하므로
  // currentTime이 duration을 넘는 것이 정상이고, 종료 확정(finalize)을 적용하면
  // 재생 중인 루프를 끊고 청취 시간을 되돌리는 오동작이 된다. 라이브 트랙의
  // 완료(isEnded)는 재생 중 duration-30 규칙이 이미 처리한다.
  isLiveTrack: boolean;
}

// 메시지 두절 동안에도 네이티브(TrackPlayer)는 백그라운드에서 계속 재생했다고 보고
// 벽시계 경과로 현재 위치를 추정한다. lastKnownTimeSec가 floor된 값이라 추정치는
// 실제보다 작거나 같다(보수적) — 추정치가 duration에 도달했다면 실제로 끝난 것.
export const estimateCurrentPositionSec = (
  lastKnownTimeSec: number,
  lastMessageAtMs: number,
  nowMs: number
): number => lastKnownTimeSec + Math.max(0, nowMs - lastMessageAtMs) / 1000;

export const decideStallRecovery = ({
  isActive,
  playerStatus,
  lastMessageAtMs,
  nowMs,
  lastKnownTimeSec,
  durationSec,
  isDocumentVisible,
  unansweredStateQueries,
  isLiveTrack,
}: StallRecoveryInput): StallRecoveryAction => {
  const isPlayingLike =
    playerStatus === "playing" ||
    playerStatus === "buffering" ||
    playerStatus === "loading";

  if (!isActive || !isPlayingLike) return "none";
  if (lastMessageAtMs == null) return "none";
  // WebView가 숨겨진 동안은 메시지 두절이 정상일 수 있으므로 복귀 후에만 판단
  if (!isDocumentVisible) return "none";
  if (nowMs - lastMessageAtMs < STALL_THRESHOLD_MS) return "none";

  // 실측 우선: 신버전 바이너리는 getState에 실제 상태(finished/paused/...)로
  // 응답하므로 항상 질의를 먼저 시도한다. 벽시계 추정(finalize)은 질의가
  // 무응답일 때(구버전 바이너리)의 최후 수단 — 백그라운드 중 알림 일시정지
  // 등이 유실된 경우 추정이 오완료를 만드는 것을 구조적으로 차단한다.
  if (unansweredStateQueries < MAX_UNANSWERED_STATE_QUERIES) {
    return "queryState";
  }

  const estimated = estimateCurrentPositionSec(
    lastKnownTimeSec,
    lastMessageAtMs,
    nowMs
  );
  if (
    !isLiveTrack &&
    durationSec > 0 &&
    lastKnownTimeSec > 0 &&
    estimated >= durationSec
  ) {
    return "finalize";
  }

  return "downgradeToPaused";
};
